/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable no-param-reassign */
import { useContext, useEffect, useMemo, useState } from 'react';
import { Add as AddIcon } from '@mui/icons-material';
import CloseIcon from '@mui/icons-material//Close';
import { Button, Collapse, IconButton } from '@mui/material';
import Alert from '@mui/material/Alert';
import { isEmpty } from 'lodash';
import uuid from 'react-uuid';
import { SYSTEM_EXPAND } from 'common/actions/row-groups/types';
import {
  CONVERTIBLE_NOTES_ROW_NUMBER,
  ISSUE_PRICE_ROW_NUMBER,
  PREFERRED_TERMS_ALIAS,
  SHARES_FULLY_DILUTED_ROW_NUMBER,
  SHARES_OUTSTANDING_ROW_NUMBER,
} from 'common/constants/cap-table';
import { FIX_BEFORE_CONTINUE } from 'common/constants/messages/validations';
import { useStore } from 'common/store';
import { UseStoreValues } from 'common/types/store';
import { EmptyTableMessage, InfoCard } from 'components';
import { LedgerDialog } from 'components/Dialogs';
import FeaturedSpreadsheetContext from 'components/FeaturedSpreadsheet/context/FeaturedSpreadsheetContext';
import useWorkbook from 'components/ScalarSpreadsheet/utilities/useWorkbook';
import WorkbookContext from 'components/ScalarSpreadsheet/utilities/WorkbookContext';
import { useCapTableContext } from 'context/CapTableContext';
import useConvertibleNoteSheet from 'pages/CapTable/cap-table/components/convertible-notes-ledger/config/useConvertibleNoteSheet/useConvertibleNoteSheet';
import { convertibleNoteTemplate, customValidations } from './config';
import { PREFERRED_STOCK_VALUE, TOTAL_SHARES_OUTSTANDING_KEY } from './config/constants';
import { ConvertibleNotesLedgerProps } from './ConvertibleNotesLedger.props';
import { AddColumnButtonContainer, InfoCardsContainer, StyledScalarSpreadsheet } from './ConvertibleNotesLedger.styles';
import { ConvertibleNotesLedgerCell } from './types';

const InfoCards = () => (
  <InfoCardsContainer>
    <InfoCard title="Notes Ledger" body={' '} />
  </InfoCardsContainer>
);

const tableTerms = {
  tableName: 'Note Ledger',
  tableSlug: 'convertible-note-ledger',
  columnName: 'Note',
  pluralColumnName: 'data',
};

const ConvertibleNoteLedger = ({ cell, closeDialog, isDisabled}: ConvertibleNotesLedgerProps) => {
  const { cells, onCellsChanged, setRowGroups } = useContext(FeaturedSpreadsheetContext);
  const { selectedMeasurementDate, getLinkedCellUpdates, convertibleNotesFieldAttrs } = useCapTableContext();
  const [{ captableInfo }] = useStore() as unknown as UseStoreValues;
  const totalSharesOutstanding = cells[TOTAL_SHARES_OUTSTANDING_KEY]?.value;
  const [columns, setColumns] = useState<ConvertibleNotesLedgerCell[]>([]);
  const [visibleColumns, setVisibleColumns] = useState<ConvertibleNotesLedgerCell[]>([]);
  const [isAlertVisible, setIsAlertVisible] = useState(false);
  const [disableSaveButton, setDisableSaveButton] = useState(true);

  const { columnLegend } = cell;

  const convertibleNoteKey = useMemo(() => columnLegend + CONVERTIBLE_NOTES_ROW_NUMBER, []);
  const sharesFullyDilutedKey = useMemo(() => columnLegend + SHARES_FULLY_DILUTED_ROW_NUMBER, []);
  const sharesOutstandingKey = useMemo(() => columnLegend + SHARES_OUTSTANDING_ROW_NUMBER, []);

  const issuePriceKey = useMemo(() => columnLegend + ISSUE_PRICE_ROW_NUMBER, []);
  const { sheet } = useConvertibleNoteSheet({
    selectedMeasurementDate,
    columns: visibleColumns,
    convertibleNotesFieldAttrs,
    isDisabled,
    fundOwnership: captableInfo?.fund_ownership,
  });

  const sheets = useMemo(() => [sheet], [sheet]);

  const { onChange: onWorkbookChange, workbook, areCellsValid, workbookContextValue } = useWorkbook(sheets);

  useEffect(() => {
    if (columns) {
      const visibleColumnsTemp = columns.filter(({ is_deleted }) => !is_deleted);
      setVisibleColumns(visibleColumnsTemp);
    }
  }, [columns]);

  const deletedColumns = useMemo(() => {
    if (columns?.length) {
      return columns.filter(column => column.is_deleted && column.id > 0);
    }
    return [];
  }, [columns]);

  const addColumn = () => {
    const newConvertibleNote = {
      ...convertibleNoteTemplate,
      security_group_ref: uuid(),
      pre_money_share_count: totalSharesOutstanding,
    };

    setColumns(prevColumns => [newConvertibleNote, ...prevColumns] as ConvertibleNotesLedgerCell[]);
  };

  const deleteConvertibleNote = (columnIndex: number) => {
    setColumns(prevColumns => {
      prevColumns[columnIndex].is_deleted = true;
      return [...prevColumns];
    });
  };

  useEffect(() => {
    if (visibleColumns.length > 0) {
      setDisableSaveButton(false);
    } else {
      setDisableSaveButton(true);
    }
  }, [visibleColumns]);

  const save = () => {
    if (areCellsValid()) {
      const convertibleNotes = columns;

      const tmpState = { ...cells };
      const changes = [];

      // Reset "Shares Outstanding" and "Original Issue Price"
      if (isEmpty(convertibleNotes)) {
        // Reset "Original Issue Price"
        changes.push({
          cell: tmpState[issuePriceKey],
          value: tmpState[issuePriceKey].defaultValue,
        });
        // Reset "Shares Outstanding"
        changes.push({
          cell: tmpState[sharesFullyDilutedKey],
          value: tmpState[sharesFullyDilutedKey].defaultValue,
        });
      } else {
        // The Original Issue Price (row 4), should be the sum of all conversion price per share
        const totalOriginalIssuePrice = convertibleNotes
          .map(convertibleNote => Number(convertibleNote.conversion_price_per_share))
          .reduce((previous, current) => previous + current, 0);
        changes.push({
          cell: tmpState[issuePriceKey],
          value: totalOriginalIssuePrice.toString(),
        });

        // The shares outstanding (row 5), should be the sum of all expected_shares in the ledger
        const totalExpectedShares = convertibleNotes
          .map(convertibleNote => Number(convertibleNote.expected_shares))
          .reduce((previous, current) => previous + current, 0);
        changes.push({
          cell: tmpState[sharesFullyDilutedKey],
          value: totalExpectedShares.toString(),
        });
        changes.push({
          cell: tmpState[sharesOutstandingKey],
          value: totalExpectedShares.toString(),
        });
      }

      const allConvertibleNotes = [...convertibleNotes, ...deletedColumns];

      // Save Convertible Note ledger
      changes.push({
        cell: tmpState[convertibleNoteKey],
        value: allConvertibleNotes,
      });

      // Update global state
      const linkedUpdates = getLinkedCellUpdates(tmpState);

      onCellsChanged([...changes, ...linkedUpdates]);
      closeDialog();
      // if any of the convertible notes is modeled as preferred expand the preferred rows
      const containsNoteModeledAsPreferred = convertibleNotes.some(
        ({ model_as_equity }) => model_as_equity === PREFERRED_STOCK_VALUE
      );

      if (containsNoteModeledAsPreferred) {
        setRowGroups?.({ type: SYSTEM_EXPAND, row: PREFERRED_TERMS_ALIAS });
      }
    } else {
      setIsAlertVisible(true);
    }
  };

  const getInitialData = () => {
    const cellData = cells[convertibleNoteKey].value;
    const ledgerData = [...((cellData ?? []) as ConvertibleNotesLedgerCell[])];
    setColumns(ledgerData);
  };

  useEffect(() => {
    if (cell) {
      getInitialData();
    }
  }, [cell]);

  return (
    <LedgerDialog
      id="convertible-note-ledger"
      title=""
      onSave={save}
      disableSaveButton={disableSaveButton}
      onClose={closeDialog}
      dialogHeader={<InfoCards />}>
      <Collapse in={isAlertVisible}>
        <Alert
          variant="outlined"
          severity="error"
          action={
            <IconButton
              aria-label="close"
              color="inherit"
              size="small"
              onClick={() => {
                setIsAlertVisible(false);
              }}>
              <CloseIcon fontSize="inherit" />
            </IconButton>
          }>
          {FIX_BEFORE_CONTINUE}
        </Alert>
        <br />
      </Collapse>
      {columns.length === 0 && (
        <div>
          <EmptyTableMessage tableTerms={tableTerms} />
        </div>
      )}
      {columns.length > 0 && (
        <WorkbookContext.Provider value={workbookContextValue}>
          <StyledScalarSpreadsheet
            {...sheet}
            isLedgerTable
            deleteColumn={deleteConvertibleNote}
            workbook={workbook}
            validations={customValidations}
            onChange={onWorkbookChange}
            sheet={sheet}
          />
        </WorkbookContext.Provider>
      )}
      <AddColumnButtonContainer>
        <Button
          id="add-convertible-note-btn"
          size="small"
          variant="outlined"
          color="primary"
          endIcon={<AddIcon />}
          onClick={addColumn}>
          Add {tableTerms.columnName}
        </Button>
      </AddColumnButtonContainer>
    </LedgerDialog>
  );
};

export default ConvertibleNoteLedger;
